import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Steps, Space, Button, Table, Tag, message, Alert } from 'antd';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { getDebtDetail, generateNoticeBodyPdfs } from '../../services/debt';
import { extractPlaceholders } from '../../components/NoticeDocEditor/utils';
import { formatAmountText } from '../../utils/numberFormat';
import { labelOfDebtType } from '../../constants/debtEnums';
import { loadState, saveState } from './PublishNoticeEditContent/state';
import { useBreakpoint } from '../../hooks/useBreakpoint';

/**
 * 催告发布步骤5：生成催告函
 */
const PublishNoticeGeneratePdfPage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const state = loadState();
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if ((!state.selectedDebtId && !id) || !state.saved) {
      navigate('/debt-notice/publish');
    }
  }, [id, navigate, state.saved, state.selectedDebtId]);

  useEffect(() => {
    const debtId = id || state.selectedDebtId;
    if (!debtId) return;
    getDebtDetail(debtId).then(({ data }) => setSelectedDebt(data)).catch(() => {});
  }, [id, state.selectedDebtId]);

  const prev = () => {
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/signature/${debtId}`);
  };

  const next = () => {
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/send/${debtId}`);
  };

  const selectedDebtors = useMemo(() => {
    const list = Array.isArray(state.selectedDebtors) ? state.selectedDebtors : [];
    return list.filter(Boolean);
  }, [state.selectedDebtors]);

  const placeholders = useMemo(() => {
    try {
      return extractPlaceholders(String(state.contentHtml || ''));
    } catch {
      return [];
    }
  }, [state.contentHtml]);

  const placeholderFieldMap = useMemo(() => (state.placeholderFieldMap || {}), [state.placeholderFieldMap]);
  const placeholderManualMap = useMemo(() => (state.placeholderManualMap || {}), [state.placeholderManualMap]);

  const buildValueMapForDebtor = useCallback((debtorRow) => {
    const debt = selectedDebt?.debt || {};
    const creditor = selectedDebt?.creditor || {};
    const debtor = debtorRow || {};

    const today = dayjs().format('YYYY年MM月DD日');
    const debtOccurrenceDate = debt?.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate).format('YYYY年MM月DD日') : '';

    const creditorType = String(creditor?.creditorType || '');
    const creditorName = creditorType === 'P' ? (creditor?.name || '') : (creditor?.companyName || '');
    const creditorIdNo = creditorType === 'P' ? (creditor?.idCardNo || '') : (creditor?.socialCreditCode || '');

    const debtorType = String(debtor?.type || '');
    const debtorName = debtorType === 'P' ? (debtor?.name || '') : (debtor?.companyName || '');
    const debtorIdNo = debtorType === 'P' ? (debtor?.idNo || '') : (debtor?.socialCreditCode || '');

    return {
      'today': today,
      'debt.debtNo': debt?.debtNo || '',
      'debt.title': debt?.title || '',
      'debt.debtType': labelOfDebtType(debt?.debtType),
      'debt.debtOccurrenceDate': debtOccurrenceDate,
      'debt.principalAmount': formatAmountText(debt?.principalAmount),
      'debt.totalAmount': formatAmountText(debt?.totalAmount),

      'creditor.name': creditorName,
      'creditor.idNo': creditorIdNo,
      'creditor.phone': creditor?.phone || '',
      'creditor.email': creditor?.email || '',
      'creditor.companyName': creditor?.companyName || '',
      'creditor.socialCreditCode': creditor?.socialCreditCode || '',
      'creditor.legalPersonName': creditor?.legalPersonName || '',
      'creditor.legalPersonIdNo': creditor?.legalPersonIdNo || '',
      'creditor.province': creditor?.province || '',
      'creditor.city': creditor?.city || '',
      'creditor.address': creditor?.address || '',

      'debtor.name': debtorName,
      'debtor.idNo': debtorIdNo,
      'debtor.phone': debtor?.phone || '',
      'debtor.email': debtor?.email || '',
      'debtor.companyName': debtor?.companyName || '',
      'debtor.socialCreditCode': debtor?.socialCreditCode || '',
      'debtor.legalPersonName': debtor?.legalPersonName || '',
      'debtor.legalPersonIdNo': debtor?.legalPersonIdNo || '',
    };
  }, [selectedDebt]);

  const buildPlaceholderValuesForDebtor = useCallback((debtorRow) => {
    const phList = Array.isArray(placeholders) ? placeholders : [];
    const valueMap = buildValueMapForDebtor(debtorRow);
    const out = {};
    phList.forEach((ph) => {
      const manual = String((placeholderManualMap || {})[ph] || '').trim();
      if (manual) {
        out[ph] = manual;
        return;
      }
      const fk = String((placeholderFieldMap || {})[ph] || '').trim();
      if (!fk) {
        out[ph] = '';
        return;
      }
      const v = valueMap[fk];
      out[ph] = v === undefined || v === null ? '' : String(v);
    });
    return out;
  }, [buildValueMapForDebtor, placeholders, placeholderFieldMap, placeholderManualMap]);

  const canNext = records.length > 0;

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告函' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 4;

  const handleGenerate = async () => {
    const debtId = id || state.selectedDebtId;
    if (!debtId) return;
    if (!state.saved) {
      message.warning('请先在上一步校验并保存');
      return;
    }
    if (!state.contentHtml || String(state.contentHtml).trim().length < 10) {
      message.warning('催告内容为空');
      return;
    }
    if (!selectedDebtors.length) {
      message.warning('请至少选择一个债务人');
      return;
    }
    if (!selectedDebt) {
      message.warning('债权详情未加载完成，请稍后重试');
      return;
    }
    setLoading(true);
    try {
      const recipients = selectedDebtors.map((d) => {
        const receiverName = d?.type === 'E' ? (d?.companyName || '') : (d?.name || '');
        return {
          debtorKey: d?.key,
          debtorType: d?.type,
          receiverName,
          receiverPhone: d?.phone || '',
          receiverEmail: d?.email || '',
          renderValues: buildPlaceholderValuesForDebtor(d),
        };
      });
      const signature = {
        applySignature: !!state.applySignature,
        sealId: state.sealId || null,
        sealWidthMm: Number(state.sealWidthMm || 30),
        sealAspect: Number(state.sealAspect || 1),
        sealOffsetRightMm: Number.isFinite(Number(state.sealOffsetRightMm)) ? Number(state.sealOffsetRightMm) : 8,
        sealOffsetBottomMm: Number.isFinite(Number(state.sealOffsetBottomMm)) ? Number(state.sealOffsetBottomMm) : 8,
      };
      const payload = {
        contentHtml: state.contentHtml,
        pageSettings: state.pageSettings,
        signature,
        recipients,
      };
      const { data } = await generateNoticeBodyPdfs(debtId, payload);
      const list = Array.isArray(data?.records) ? data.records : [];
      setRecords(list);
      saveState({ generatedPdfRecords: list });
      message.success(`已生成 ${list.length} 份PDF`);
    } catch (e) {
      message.error(e?.message || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <h1 className="page-title">发布催告</h1>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
                <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
              </div>
              <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
            </div>
          ) : (
            <Steps current={currentStep} size="small" items={steps} />
          )}
        </Card>

        <div className="mb-md">
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            style={{ width: isMobile ? '100%' : undefined }}
          >
            <Button onClick={prev} block={isMobile}>上一步</Button>
            <Button type="primary" onClick={next} disabled={!canNext} block={isMobile}>下一步</Button>
          </Space>
        </div>

        <Card>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {!state.saved ? <Alert type="warning" showIcon message="尚未保存" description="请先回到上一步“校验并保存”，再生成PDF。" /> : null}
            {selectedDebtors.length === 0 ? <Alert type="warning" showIcon message="未选择债务人" description="请回到上一步选择至少一个债务人。" /> : null}
            {state.applySignature && !state.sealId ? <Alert type="warning" showIcon message="已启用签章但未选择印签" description="请回到上一步选择印签。" /> : null}

            <Space wrap>
              <Button type="primary" onClick={handleGenerate} loading={loading} disabled={!state.saved || selectedDebtors.length === 0}>
                生成PDF
              </Button>
              <Tag color={state.applySignature ? 'blue' : 'default'}>{state.applySignature ? '已启用签章' : '未启用签章'}</Tag>
              <Tag color={placeholders.length ? 'green' : 'default'}>{`占位符 ${placeholders.length} 个`}</Tag>
              <Tag color={selectedDebtors.length ? 'green' : 'default'}>{`债务人 ${selectedDebtors.length} 个`}</Tag>
            </Space>

            <Table
              size="small"
              rowKey={(r) => String(r?.pdfObjectKey || r?.debtorKey || Math.random())}
              dataSource={records}
              pagination={false}
              columns={[
                {
                  title: '债务人',
                  dataIndex: 'debtorKey',
                  key: 'debtorKey',
                  width: 260,
                  render: (_, row) => {
                    const key = String(row?.debtorKey || '');
                    const src = selectedDebtors.find((d) => String(d?.key) === key) || null;
                    if (!src) return key || '-';
                    return src.type === 'E'
                      ? `${src.companyName || '企业'} (${src.socialCreditCode || '-'})`
                      : `${src.name || '个人'} (${src.idNo || '-'})`;
                  },
                },
                { title: '手机号', dataIndex: 'receiverPhone', key: 'receiverPhone', width: 150, render: (v) => v || '-' },
                { title: '邮箱', dataIndex: 'receiverEmail', key: 'receiverEmail', width: 220, render: (v) => v || '-' },
                {
                  title: '文件',
                  dataIndex: 'pdfUrl',
                  key: 'pdfUrl',
                  render: (_, row) => (
                    <Space>
                      <Button
                        size="small"
                        onClick={() => {
                          if (!row?.pdfUrl) return;
                          window.open(row.pdfUrl, '_blank', 'noopener,noreferrer');
                        }}
                        disabled={!row?.pdfUrl}
                      >
                        下载/预览
                      </Button>
                      {row?.pdfObjectKey ? <Tag color="geekblue">已上传</Tag> : <Tag>未上传</Tag>}
                    </Space>
                  ),
                },
              ]}
            />
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default PublishNoticeGeneratePdfPage;
